import { useEffect, useState } from 'react'
import { Box, Flex, Link, useBreakpointValue } from '@chakra-ui/react'
import { Btn } from '@/components/ui/Btn'
import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { bookingUrl } from '@/config/site'
import { useLocale } from '@/i18n'
import { CONTACT_INFO } from '@/components/sections/Contact/contact.data'
import { NAV_COPY } from './nav.data'

const BAR_TRANSITION = 'transform 160ms ease, opacity 160ms ease'

const BurgerBar = (props: { transform?: string; opacity?: number }) => (
  <Box
    h="2px"
    bg="ink"
    transition={BAR_TRANSITION}
    transform={props.transform}
    opacity={props.opacity}
  />
)

export const Nav = () => {
  const { locale } = useLocale()
  const nav = NAV_COPY[locale]
  const [menuOpen, setMenuOpen] = useState(false)
  const isDesktop = useBreakpointValue({ base: false, lg: true })

  useEffect(() => {
    if (isDesktop && menuOpen) setMenuOpen(false)
  }, [isDesktop, menuOpen])

  useEffect(() => {
    if (!menuOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  const closeMenu = () => {
    setMenuOpen(false)
  }

  return (
    <>
      <Flex
        as="nav"
        position="sticky"
        top="0"
        zIndex="20"
        bg="ground"
        borderBottom="2px solid"
        borderColor="divider"
        align="center"
        gap="clamp(12px, 2vw, 32px)"
        px="clamp(20px, 3vw, 48px)"
        h="64px"
      >
        <Link
          href="#top"
          fontFamily="heading"
          fontWeight="800"
          fontSize="15px"
          letterSpacing="0.14em"
          textTransform="uppercase"
          color="ink"
          textDecoration="none"
          whiteSpace="nowrap"
          flexShrink={0}
        >
          Garlic Tech
        </Link>
        <Flex
          display={{ base: 'none', lg: 'flex' }}
          gap="clamp(12px, 1.6vw, 26px)"
          align="center"
          ml="clamp(4px, 1vw, 16px)"
          flexWrap="nowrap"
          minW="0"
        >
          {nav.links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              fontSize="clamp(11px, 1.05vw, 13px)"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color="ink"
              textDecoration="none"
              whiteSpace="nowrap"
              _hover={{ color: 'accent' }}
            >
              {link.label}
            </Link>
          ))}
        </Flex>
        <Flex
          ml="auto"
          align="center"
          gap="clamp(12px, 1.6vw, 20px)"
          flexShrink={0}
          whiteSpace="nowrap"
        >
          <LanguageToggle />
          <Btn
            href={bookingUrl('build', 'hero')}
            target="_blank"
            rel="noopener noreferrer"
            css={{ '@media (max-width: 399px)': { display: 'none' } }}
            px={{ base: '12px', lg: '16px' }}
            py={{ base: '9px', lg: '10px' }}
            fontSize={{ base: '12px', lg: 'clamp(12px, 1.05vw, 14px)' }}
          >
            {nav.ctaLabel}
            <Box as="span" display={{ base: 'none', lg: 'inline' }}>
              {' →'}
            </Box>
          </Btn>
          <Flex
            as="button"
            aria-label={nav.menuLabel}
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((open) => !open)
            }}
            display={{ base: 'flex', lg: 'none' }}
            direction="column"
            justify="center"
            gap="5px"
            w="40px"
            h="40px"
            px="8px"
            bg="none"
            border="2px solid"
            borderColor="divider"
            cursor="pointer"
          >
            <BurgerBar transform={menuOpen ? 'translateY(7px) rotate(45deg)' : undefined} />
            <BurgerBar opacity={menuOpen ? 0 : 1} />
            <BurgerBar transform={menuOpen ? 'translateY(-7px) rotate(-45deg)' : undefined} />
          </Flex>
        </Flex>
      </Flex>
      {menuOpen && (
        <Flex
          display={{ base: 'flex', lg: 'none' }}
          position="fixed"
          top="64px"
          left="0"
          right="0"
          zIndex="19"
          maxH="calc(100vh - 64px)"
          overflowY="auto"
          direction="column"
          bg="ground"
          borderBottom="2px solid"
          borderColor="divider"
          pt="8px"
          px="clamp(20px, 4vw, 48px)"
          pb="28px"
        >
          {nav.links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={closeMenu}
              borderBottom="2px solid"
              borderColor="divider"
              py="18px"
              fontFamily="heading"
              fontWeight="800"
              fontSize="20px"
              letterSpacing="-0.01em"
              color="ink"
              textDecoration="none"
              _hover={{ color: 'accent' }}
            >
              {link.label}
            </Link>
          ))}
          <Btn
            href={bookingUrl('ai-audit', 'contact')}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
            variant="secondary"
            block
            mt="24px"
            fontSize="15px"
            px="18px"
            py="16px"
          >
            {CONTACT_INFO[locale].auditCtaLabel}
          </Btn>
        </Flex>
      )}
    </>
  )
}
